import { KeyValuePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  linkedSignal,
  resource,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, NgModel, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  ClrButtonModule,
  ClrDatagridModule,
  ClrFormsModule,
  ClrIcon,
  ClrLoadingModule,
  ClrLoadingState,
  ClrModalModule,
  ClrSidePanelModule,
  ClrSpinnerModule,
  ClrTooltipModule,
  ClrTreeViewModule,
} from '@clr/angular';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { APIService } from 'src/app/services/APIService/api.service';
import { ConfirmService } from 'src/app/services/confirm.service';
import { FormGroupOfType, Numerated } from 'src/app/types/shared';
import {
  BaseItemsWrapperComponent,
  BaseDataRoute,
  BaseDataTable,
  BaseItem,
} from '../../_modals/baseitems-wrapper/baseitems-wrapper.component';
import { DatalistInputComponent } from '../../_shared/datalist-input/datalist-input.component';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { errorHandle, getTokenRight } from 'src/app/misc/functions';
import { GetByCallbackPipe } from 'src/app/pipes/get-by-callback.pipe';
import { Row } from 'src/app/models/ui/table';
import { AuthService } from 'src/app/services/Auth/auth.service';

@Component({
  selector: 'app-mangelbaum',
  templateUrl: './mangelbaum.component.html',
  styleUrl: './mangelbaum.component.scss',
  imports: [
    ClrFormsModule,
    ClrIcon,
    ClrSidePanelModule,
    ClrTooltipModule,
    ClrTreeViewModule,
    ClrSpinnerModule,
    ClrButtonModule,
    ClrLoadingModule,
    ClrModalModule,
    ClrDatagridModule,
    FormsModule,
    ReactiveFormsModule,
    MrTranslatePipe,
    KeyValuePipe,
    HideIDColumnsPipe,
    TablePrettyPrintPipe,
    GetByCallbackPipe,
    BaseItemsWrapperComponent,
    DatalistInputComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MangelbaumComponent {
  readonly #apiService = inject(APIService);
  readonly #authService = inject(AuthService);
  readonly #mrTranslate = inject(MrTranslatePipe);
  readonly #toastr = inject(ToastrService);
  readonly #cdr = inject(ChangeDetectorRef);
  readonly #confirm = inject(ConfirmService);

  public readonly closedialog = new Subject<boolean>();

  protected readonly objekte = toSignal(this.#apiService.getSOTYP());
  protected readonly otypid = signal<number>(undefined);
  protected readonly selectedObjektTyp = computed(() => this.objekte()?.[this.otypid()]);
  protected readonly isWeiche = computed(() => this.otypid() == 1);
  protected readonly isGleis = computed(() => this.otypid() == 2);

  protected readonly isBaseModalOpen = signal(true);
  protected readonly saveState = signal(ClrLoadingState.DEFAULT);

  //#region Tree
  protected readonly isBaumPanelOpen = signal(true);
  protected allowMBCreate = computed(() => getTokenRight(this.rights().type[this.otypid()], 'MGVADD'));
  protected rights = computed(() => JSON.parse(this.#authService.decrypt(localStorage.getItem('rights'))));
  protected readonly selected = linkedSignal<MangelBaumNode>(() => (this.otypid(), undefined));
  protected editingId: number;
  protected readonly treeLevel0 = resource({
    params: () => this.otypid(),
    loader: ({ params: otypid }) => this.#apiService.getMangelBaum(otypid),
    defaultValue: [],
  });

  protected readonly getChildren = async (node: MangelBaumNode) => {
    let nodes: MangelBaumNode[] = [];
    try {
      nodes = await this.#apiService.getMangelBaum(this.otypid(), joinIds(node));
    } catch (er) {
      this.#toastr.error(errorHandle(er), this.#mrTranslate.transform('Etwas ist schief gelaufen'));
    }
    setTimeout(() => this.#cdr.markForCheck(), 200); //* für Entfernen von Spinner
    return nodes;
  };
  //#endregion

  //#region Options
  private readonly optionsTypRs = this.#apiService.getTypOptions(
    this.otypid,
    'Opt_I_Fehlerklasse',
    'Opt_I_Zustaend_Abt'
  );
  protected readonly useFK = computed(() => this.optionsTypRs.value()?.Opt_I_Fehlerklasse);
  protected readonly useAbteilung = computed(() => this.optionsTypRs.value()?.Opt_I_Zustaend_Abt);

  protected readonly interfaceOptionsRs = this.#apiService.getInterfaceOptions(
    'optSAPFieldEquipment',
    'optZFeld1Required',
    'optZFeld2Required',
    'optZFeld3Required',
    'optZFeld4Required',
    'optZFeld5Required',
    'optZFeld1Title',
    'optZFeld2Title',
    'optZFeld3Title',
    'optZFeld4Title',
    'optZFeld5Title'
  );

  protected readonly zFelds = computed<KundenFelds>(() => {
    const map = {} as KundenFelds;
    if (!this.interfaceOptionsRs.hasValue()) return map;
    const opts = this.interfaceOptionsRs.value();

    if (opts.optZFeld1Required) map.ZFeld1 = opts.optZFeld1Title;
    if (opts.optZFeld2Required) map.ZFeld2 = opts.optZFeld2Title;
    if (opts.optZFeld3Required) map.ZFeld3 = opts.optZFeld3Title;
    if (opts.optZFeld4Required) map.ZFeld4 = opts.optZFeld4Title;
    if (opts.optZFeld5Required) map.ZFeld5 = opts.optZFeld5Title;

    return map;
  });

  protected readonly hideZFelds = (col: string) => (/zfeld/i.test(col) ? !this.zFelds()[col] : col);
  protected readonly useEquipment = computed(() =>
    this.interfaceOptionsRs.hasValue() ? this.interfaceOptionsRs.value().optSAPFieldEquipment : false
  );

  protected readonly rangeFK = this.#apiService.getRangeFK();
  //#region Form

  protected readonly form = new FormGroupOfType<MangelBaumNodeModel>({
    ID: new FormControl<number>(null),
    GattungID: new FormControl<number>(null), //* dynamisch Required + disabled on non-Gleis
    StammID: new FormControl<number>(null, Validators.required), //* disabled on non-Gleis
    PruefartID: new FormControl<number>(null, Validators.required),
    LageID: new FormControl<number>(null, Validators.required), //* disabled on Gleis
    MangelID: new FormControl<number>(null, Validators.required),
    InstandsetzungID: new FormControl<number>(null, Validators.required),
    FK: new FormControl<number>(null), //* dynamisch Required
    MengeneinheitID: new FormControl<number>(null),
    Bedingung: new FormControl<boolean>(false, { nonNullable: true }),
    BedAusloeserMin: new FormControl<Bedingung>(null, Validators.required),
    BedMin: new FormControl<number>(null, [Validators.required, Validators.min(0)]),
    AndBedingung: new FormControl<boolean>(false, { nonNullable: true }),
    BedAusloeserMax: new FormControl<Bedingung>(null, Validators.required),
    BedMax: new FormControl<number>(null, [Validators.required, Validators.min(0)]),
    BedEinheit: new FormControl<BedingungEinheit>(null, Validators.required),
    Preis: new FormControl<number>(null, Validators.min(0)),
    Abteilung: new FormControl<string>(null), //* dynamisch Required
    Equipment: new FormControl<string>(null), //* dynamisch Required
    LVPos: new FormControl<string>(null),
    ZFeld1: new FormControl<string>(null), //* dynamisch Required
    ZFeld2: new FormControl<string>(null), //* dynamisch Required
    ZFeld3: new FormControl<string>(null), //* dynamisch Required
    ZFeld4: new FormControl<string>(null), //* dynamisch Required
    ZFeld5: new FormControl<string>(null), //* dynamisch Required
  });

  protected readonly gattungId = toSignal(this.form.controls.GattungID.valueChanges);
  protected readonly id = toSignal(this.form.controls.ID.valueChanges);
  //#endregion

  //#region Select Tabellen
  protected readonly selectUsedRs = this.#apiService.getMangelBaumUsed();
  protected readonly used = this.selectUsedRs.value;
  private readonly selectResource = this.#apiService.getMangelBaumSelect(this.otypid);

  protected readonly gattungen = computed(() => this.selectResource.value().Gattungen);
  protected readonly stammItems = computed(() => this.selectResource.value().StammItems);
  protected readonly pruefarten = computed(() => this.selectResource.value().Prüfarten);
  protected readonly lagen = computed(() => this.selectResource.value().Lagen);
  protected readonly maengel = computed(() => this.selectResource.value().Mängel);
  protected readonly instandsetzungen = computed(() => this.selectResource.value().Instandsetzungen);
  protected readonly meh = computed(() => this.selectResource.value().Mengeneinheiten);
  protected readonly equipments = computed(() => this.selectResource.value().Equipment);

  protected readonly editTabelle = signal<BaseDataRoute>(null);

  protected readonly stammTable = computed<BaseDataTable>(() => {
    if (this.gattungId() > 10) return 'SSTAMM';
    switch (this.gattungId()) {
      case 2:
        return 'SBEFT';
      case 3:
        return 'SSCHW';
      case 5:
        return 'SEIND';
      case 7:
        return 'SBAUW';
      default:
        return null;
    }
  });
  protected readonly stammTableTitle = computed(() => {
    switch (this.stammTable()) {
      case 'SBEFT':
        return 'Befestigung';
      case 'SSCHW':
        return 'Schwellenart';
      case 'SEIND':
        return 'Eindeckung';
      case 'SBAUW':
        return 'Bauwerke';
      case 'SSTAMM':
        return this.gattungen().find(item => item.ID == this.gattungId())?.Bezeichnung || 'Stammitems';
      default:
        return null;
    }
  });
  //#endregion
  //#region Historie
  protected readonly isHistorieModalOpen = signal(false);
  protected readonly historieId = signal<number>(undefined);
  protected readonly historie = this.#apiService.getMangelBaumHistorie(() =>
    this.isHistorieModalOpen() ? this.historieId() : undefined
  );
  //#endregion
  //#region Table Ansicht
  protected readonly isListeMode = signal(false);
  protected readonly liste = this.#apiService.getMangelBaumInTable(() =>
    this.isListeMode() ? this.otypid() : undefined
  );
  //#endregion
  protected readonly BAUM_ICON = BAUM_ICON;
  protected readonly BEDINGUNGEN = BEDINGUNGEN;
  protected readonly BED_EINHEITEN = BED_EINHEITEN;
  protected readonly CELL_CONTROLS = CELL_CONTROLS;
  protected readonly BAUM_LEVEL = MangelBaumLevel;

  public set data(dataIn: any) {
    this.otypid.set(dataIn.otypid || 1);
  }

  constructor() {
    effect(() => (this.otypid(), this.form.reset()));
    effect(() => {
      this.activateControlIf('GattungID', this.isGleis() || this.isWeiche());
      this.activateControlIf('StammID', this.isGleis());
      this.activateControlIf('LageID', !this.isGleis());
      this.form.controls.GattungID.setValidators(this.isGleis() ? Validators.required : null);
    });
    const { FK, Abteilung, Equipment, Bedingung, AndBedingung } = this.form.controls;
    effect(() => FK.setValidators(this.useFK() ? Validators.required : null));
    effect(() => Abteilung.setValidators(this.useAbteilung() ? Validators.required : null));
    effect(() => Equipment.setValidators(this.useEquipment() ? Validators.required : null));

    effect(() => {
      if (!this.interfaceOptionsRs.hasValue()) return;
      const opts = this.interfaceOptionsRs.value();

      const { ZFeld1, ZFeld2, ZFeld3, ZFeld4, ZFeld5 } = this.form.controls;
      ZFeld1.setValidators(opts.optZFeld1Required ? Validators.required : null);
      ZFeld2.setValidators(opts.optZFeld2Required ? Validators.required : null);
      ZFeld3.setValidators(opts.optZFeld3Required ? Validators.required : null);
      ZFeld4.setValidators(opts.optZFeld4Required ? Validators.required : null);
      ZFeld5.setValidators(opts.optZFeld5Required ? Validators.required : null);
    });

    Bedingung.valueChanges.pipe(takeUntilDestroyed()).subscribe(bedingung => {
      this.activateControlIf('BedAusloeserMin', bedingung);
      this.activateControlIf('BedMin', bedingung);
      this.activateControlIf('AndBedingung', bedingung);
      this.activateControlIf('BedEinheit', bedingung);
    });

    AndBedingung.valueChanges.pipe(takeUntilDestroyed()).subscribe(andBedingung => {
      this.activateControlIf('BedAusloeserMax', andBedingung);
      this.activateControlIf('BedMax', andBedingung);
    });
  }

  protected async editNode() {
    try {
      const eintrag = await this.#apiService.getMangelBaumEintrag(this.editingId);
      this.form.reset(eintrag);
    } catch (er) {
      this.#toastr.error(errorHandle(er), this.#mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  protected copyNode() {
    const [lvl0, lvl1, lvl2, lvl3, lvl4] = [...this.selected().Route.ids, this.selected().ID];
    if (this.isGleis()) {
      this.form.reset({
        GattungID: lvl0,
        StammID: lvl1,
        PruefartID: lvl2,
        MangelID: lvl3,
        InstandsetzungID: lvl4,
      });
    } else {
      this.form.reset({
        PruefartID: lvl0,
        LageID: lvl1,
        MangelID: lvl2,
        InstandsetzungID: lvl3,
      });
    }
  }

  protected async saveIfValid() {
    if (this.form.invalid) return;

    this.saveState.set(ClrLoadingState.LOADING);
    try {
      const { id, exist } = await this.#apiService.setMangelBaumEintrag(this.otypid(), this.clearedFields());

      if (exist) throw new Error(`${this.#mrTranslate.transform('Eintrag bereits existiert')}: ${id}`);

      this.editingId = id;
      await this.editNode();
      this.saveState.set(ClrLoadingState.SUCCESS);
      this.#toastr.success(this.#mrTranslate.transform('Erfolgreich gespeichert'));
    } catch (er) {
      this.#toastr.error(errorHandle(er), this.#mrTranslate.transform('Etwas ist schief gelaufen'));
      this.saveState.set(ClrLoadingState.DEFAULT);
    }
  }

  private clearedFields(): MangelBaumNodeModel {
    const value = this.form.value as MangelBaumNodeModel;

    value.GattungID ??= 0;
    value.StammID ??= 0;
    value.LageID ??= 0;
    value.MengeneinheitID ??= 0;
    value.ID ??= 0;
    value.Preis ??= 0;

    if (!value.Bedingung) {
      value.BedAusloeserMin = null;
      value.BedMin = 0;
      value.BedEinheit = null;
    }

    if (!value.Bedingung || !value.AndBedingung) {
      value.AndBedingung = false;
      value.BedAusloeserMax = null;
      value.BedMax = 0;
    }

    return value;
  }

  protected async deleteNode() {
    const node = this.selected();
    const isEndNode = node.Route.level == MangelBaumLevel.FK;
    const text = `Sind Sie sicher, dass Sie ${isEndNode ? 'diesen Eintrag' : 'alle zugeordnete Einträge'} löschen wollen?`;
    if (!(await this.#confirm.danger(this.#mrTranslate.transform(text)))) return;

    try {
      await this.#apiService.deleteMangelBaumTree(this.otypid(), joinIds(node));
      node.deleted = true;
      // this.#cdr.markForCheck();
      this.selected.set(undefined);
    } catch (er) {
      this.#toastr.error(errorHandle(er), this.#mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  protected editBaseItems(tabelle: keyof BaumSelect) {
    this.editTabelle.set({
      title: this.#mrTranslate.transform(tabelle),
      ...this.getBaseDataRouteFor(tabelle),
    });
    this.isBaseModalOpen.set(true);
  }

  /** @description SQL tabelle + sub_id for edit */
  private getBaseDataRouteFor(tabelle: keyof BaumSelect): BaseDataRoute {
    switch (tabelle) {
      case 'Gattungen':
        return { tabelle: 'SGATT' };
      case 'StammItems':
        return {
          tabelle: this.stammTable(),
          sub_id: this.stammTable() == 'SSTAMM' ? this.gattungId() : null,
          title: this.#mrTranslate.transform(this.stammTableTitle()),
        };
      case 'Prüfarten':
        return { tabelle: 'SPRUE', sub_id: this.otypid() };
      case 'Lagen':
        return { tabelle: 'SLAGE', sub_id: this.otypid() };
      case 'Mängel':
        return { tabelle: 'SMANG', sub_id: this.otypid() };
      case 'Instandsetzungen':
        return { tabelle: 'SINST', sub_id: this.otypid() };
      case 'Mengeneinheiten':
        return { tabelle: 'SMEH' };
      case 'Equipment':
        return { tabelle: 'KEQUI', sub_id: this.otypid() };
    }
  }

  /** @description edit in baseditems-modal */
  protected refreshTabelle(list: BaseItem[]) {
    const key = getSelectBy(this.editTabelle().tabelle);
    this.selectResource.update(select => {
      let items = select[key];
      if (Array.isArray(items)) items = list;
      else items = { ...items, [this.editTabelle().sub_id]: list };
      return { ...select, [key]: items };
    });
  }

  /** @description creates Function for insert from datalist-input */
  protected insertIn(tabelle: keyof BaumSelect) {
    return async (Bezeichnung: string) => {
      try {
        if (!(await this.#confirm.info(this.#mrTranslate.transform('Eintrag erstellen?')))) return;

        const route = this.getBaseDataRouteFor(tabelle);
        const newItem: BaseItem = { ID: null, Bezeichnung };

        newItem.ID = await this.#apiService.setBaseItems(route.tabelle, newItem, route.sub_id);

        this.selectResource.update(select => {
          let items = select[tabelle];
          if (Array.isArray(items)) items = [...items, newItem];
          else {
            items[route.sub_id].push(newItem);
            items = { ...items };
          }
          return { ...select, [tabelle]: items };
        });

        this.#toastr.success(this.#mrTranslate.transform('Erfolgreich gespeichert'));
        return newItem;
      } catch (er) {
        this.#toastr.error(errorHandle(er), this.#mrTranslate.transform('Etwas ist schief gelaufen'));
      }
    };
  }

  protected async updateField(model: NgModel, row: Row, field: string) {
    if (model.invalid) return;
    try {
      await this.#apiService.editMangelBaumField(row.ID as number, this.otypid(), field, model.value);
      row[model.name] = model.value;
      // setTimeout(() => {
      //   model.control.markAsUntouched();
      //   model.control.markAsPristine();
      // }, 5_000);
    } catch (er) {
      model.control.setErrors({ server: this.#mrTranslate.transform('Etwas ist schief gelaufen') });
      this.#toastr.error(errorHandle(er));
    }
  }

  private activateControlIf(path: keyof MangelBaumNodeModel, enabled: boolean) {
    const control = this.form.controls[path];
    if (enabled) control.enable();
    else control.disable();
  }

  protected close() {
    this.closedialog.next(true);
  }
}

interface BaumRoute {
  ids: number[];
  level: MangelBaumLevel;
}
export interface MangelBaumNode {
  ID: number;
  Bezeichnung: string;
  Route: BaumRoute;
  deleted?: boolean;
}

export enum MangelBaumLevel {
  Gattung,
  Stamm,
  Prüfart,
  Lage,
  Mangel,
  Instandsetzung,
  FK,
}

const BAUM_ICON = {
  [MangelBaumLevel.Gattung]: 'eye',
  [MangelBaumLevel.Stamm]: 'tasks',
  [MangelBaumLevel.Prüfart]: 'search',
  [MangelBaumLevel.Lage]: 'map-marker',
  [MangelBaumLevel.Mangel]: 'exclamation-triangle',
  [MangelBaumLevel.Instandsetzung]: 'cog',
  [MangelBaumLevel.FK]: 'star',
} as const satisfies Record<MangelBaumLevel, string>;

export function joinIds(node: MangelBaumNode): string {
  return [...(node.Route.ids ?? []), node.ID].join(';');
}

const BEDINGUNGEN = ['>', '<', '<=', '>='] as const;
export type Bedingung = (typeof BEDINGUNGEN)[number];
const BED_EINHEITEN = ['%', 'mm', 'Stk.'] as const;
export type BedingungEinheit = (typeof BED_EINHEITEN)[number];

export interface MangelBaumNodeIds {
  ID: number;
  GattungID: number;
  StammID: number;
  PruefartID: number;
  LageID: number;
  MangelID: number;
  InstandsetzungID: number;
}

export interface MangelBaumNodeModel extends MangelBaumNodeIds {
  FK: number;
  MengeneinheitID: number;
  Bedingung: boolean;
  BedAusloeserMin?: Bedingung;
  BedMin?: number;
  AndBedingung: boolean;
  BedAusloeserMax?: Bedingung;
  BedMax?: number;
  BedEinheit?: BedingungEinheit;
  Preis?: number;
  Abteilung?: string;
  Equipment?: string;
  LVPos?: string;
  ZFeld1?: string;
  ZFeld2?: string;
  ZFeld3?: string;
  ZFeld4?: string;
  ZFeld5?: string;
}

export interface BaumSelect {
  Gattungen: BaseItem[];
  StammItems: Record<number, BaseItem[]>;
  Prüfarten: BaseItem[];
  Lagen: BaseItem[];
  Mängel: BaseItem[];
  Instandsetzungen: BaseItem[];
  Mengeneinheiten: BaseItem[];
  Equipment: BaseItem[];
}

export const BAUM_USED_FIELDS = [
  'zuAbt',
  'Equipment',
  'LVPos',
  'ZFeld1',
  'ZFeld2',
  'ZFeld3',
  'ZFeld4',
  'ZFeld5',
] as const;
export type BaumSelectUsed = Record<(typeof BAUM_USED_FIELDS)[number], string[]>;

export const EMPTY_SELECT: BaumSelect = {
  Gattungen: [],
  StammItems: {},
  Prüfarten: [],
  Lagen: [],
  Mängel: [],
  Instandsetzungen: [],
  Mengeneinheiten: [],
  Equipment: [],
};

export const EMPTY_USED_SELECT: BaumSelectUsed = {
  zuAbt: [],
  Equipment: [],
  LVPos: [],
  ZFeld1: [],
  ZFeld2: [],
  ZFeld3: [],
  ZFeld4: [],
  ZFeld5: [],
};

function getSelectBy(tabelle: BaseDataTable): keyof BaumSelect {
  switch (tabelle) {
    case 'SGATT':
      return 'Gattungen';
    case 'SPRUE':
      return 'Prüfarten';
    case 'SLAGE':
      return 'Lagen';
    case 'SMANG':
      return 'Mängel';
    case 'SINST':
      return 'Instandsetzungen';
    case 'SMEH':
      return 'Mengeneinheiten';
    case 'KEQUI':
      return 'Equipment';
    case 'SSTAMM':
    default:
      return 'StammItems';
  }
}

const CELL_CONTROLS = {
  Preis: 'EP',
  Abnutzungsäquivalent: 'AAE',
  'Anteilige Wichtung': 'AW',
  Fehlerklasse: 'FK',
  'Gattung Weiche': 'GattungId',
  Abteilung: 'zuAbt',
  LV: 'LVPos',
  'Kav® Einheit': 'KAV_ME',
  Equipment: 'Equipment',
  ZFeld1: 'ZFeld1',
  ZFeld2: 'ZFeld2',
  ZFeld3: 'ZFeld3',
  ZFeld4: 'ZFeld4',
  ZFeld5: 'ZFeld5',
} as const;

type KundenFelds = Partial<Numerated<'ZFeld', 1 | 2 | 3 | 4 | 5, string>>;
